import { setNotification } from "features/Notification/notificationSlice";
import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import "react-quill/dist/quill.snow.css";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";
import { truncateString } from "utils/UtilityFuncs";
import CustomerSelectModal from "./QuoteDetails/CustomerSelectModal";

function QuoteAdd({ showAddModal }) {
  const navigate = useNavigate();

  /*   All States Below
   ********************************************* */

  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  const [showCustomerModal, setShowCustomerModal] = useState(false);

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();

  /*   All Functions Below
   ********************************************* */

  const handleAddQuote = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      let bodyData = {
        status: "pending",
        customerId: selectedCustomer?.id,
        locationId: selectedCustomer?.locationId,
        contactFirstName:
          selectedCustomer?.accountPayableContact?.split(" ")[0],
        contactLastName: selectedCustomer?.accountPayableContact?.split(" ")[1],
        contactPhone: selectedCustomer?.accountPayablePhone,
        contactEmail: selectedCustomer?.accountPayableEmail,
        taxRateId: selectedCustomer?.taxAccountId,
      };
      const { data } = await API.post("/quotes-add", bodyData, {
        headers: authHeader(),
      });
      if (data?.type === "success") {
        navigate(`/quotes/details/pending/${data?.data?.quoteId}?page=1`);
        showAddModal(false);
      } else {
        console.log(data);
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSelectedCustomer = (customer) => {
    setSelectedCustomer(customer);
  };

  useEffect(() => {
    const q = searchText?.trim();
    if (!q) {
      setSearchResults(null);
      setIsSearching(false);
      return;
    }

    let cancelled = false;
    setIsSearching(true);

    const timer = setTimeout(async () => {
      try {
        const { data } = await API.post(
          "/customers/search",
          { query: q },
          { headers: authHeader() }
        );
        if (!cancelled) setSearchResults(data);
      } catch (err) {
        if (!cancelled) {
          dispatch(
            setNotification({
              message: err.message,
              type: "error",
            })
          );
          setSearchResults([]);
        }
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchText, dispatch]);

  return (
    <div className="row">
      <div className="col-12">
        {/*    ADD FORM START
         ********************************************* */}

        <h3>Add Quote</h3>
        <hr className="mb-2 mt-1" />
        <form className="" onSubmit={handleAddQuote}>
          <div>
            <div className="fw-bold fs-4 mb-1 px-1">Search Customer</div>
            <div className="d-flex align-items-center mb-2">
              <div className="flex-grow-1 ">
                <input
                  className="form-control bg-white shadow-sm"
                  placeholder="Search Customer"
                  type="search"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>
            </div>

            {selectedCustomer && (
              <div className="my-3">
                <div className="fw-bold fs-4 mb-1 px-1">Selected Customer</div>
                <div
                  className="bg-white border-primary border py-2 shadow-lg"
                  style={{ borderRadius: 8 }}
                >
                  <div className="row ps-3 pe-1">
                    <div className="col-2">C-{selectedCustomer?.id}</div>
                    <div className="col-8">
                      {selectedCustomer?.customerName}
                    </div>
                    <div className="col-2">
                      <div
                        onClick={() => setSelectedCustomer(null)}
                        className="btn btn-sm btn-danger p-0 px-1"
                      >
                        <i className="mdi mdi-delete"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {searchText && (
              <div
                className="bg-white px-2 shadow-lg scroll py-2 mt-1"
                style={{
                  maxHeight: "50%",
                  scrollBehavior: "auto",
                  borderRadius: 10,
                }}
              >
                {isSearching ? (
                  <div className="px-2 py-2 text-muted d-flex align-items-center">
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Searching...
                  </div>
                ) : Array.isArray(searchResults) && searchResults.length > 0 ? (
                  searchResults.map((item, index) => (
                    <div
                      key={item?.id ?? item?.customerId ?? index}
                      className="px-2"
                    >
                      <div
                        className={` rounded py-1  ${
                          item.customerName?.toLowerCase() ===
                          searchText.toLowerCase()
                            ? "bg-primary bg-opacity-25"
                            : ""
                        } row`}
                      >
                        <div className="col-2">C-{item?.customerId}</div>
                        <div className="col-8">
                          {truncateString(item?.customerName, 35)}
                        </div>
                        <div className="col-2">
                          <div
                            onClick={() => handleSelectedCustomer(item)}
                            className="btn btn-sm btn-success p-0 px-1"
                          >
                            Select
                          </div>
                        </div>
                      </div>
                      {index < searchResults.length - 1 && (
                        <hr className="my-0 " />
                      )}
                    </div>
                  ))
                ) : (
                  <div className="px-2 py-2 text-muted">No results</div>
                )}
              </div>
            )}
          </div>
          {/*   Action Buttons
           ********************************************* */}
          <div className={`mt-3 mb-2 d-flex justify-content-end`}>
            <div>
              <button
                className="btn px-1 btn-secondary"
                type="button"
                onClick={() => {
                  showAddModal(false);
                }}
              >
                Cancel
              </button>
              <button
                disabled={!selectedCustomer || loading}
                className="btn btn-primary ms-1"
                type="submit"
              >
                Add
              </button>
            </div>
          </div>
        </form>

        {/*   Customer Select Modal
         ********************************************* */}
        <Modal
          size="xl"
          show={showCustomerModal}
          onHide={() => setShowCustomerModal(false)}
        >
          <Modal.Body>
            <div>
              <CustomerSelectModal
                showModal={setShowCustomerModal}
                handleSelectedCustomer={handleSelectedCustomer}
              />
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </div>
  );
}

export default QuoteAdd;
